import { z, ZodTypeAny } from 'zod'

// 10 colors with high contrast to white (at least 3:1)
const upperCaseHighContrastColors = [
    '#FFCC01', // DHL YELLOW
    '#D40512', // DHL RED
    '#57068C', // NYU STERN Purple
    '#000000', // Black
    '#00538A', // Strong Blue
    '#008744', // Strong Green
    '#FF6800', // Vivid Orange
    '#817066', // Medium Gray
    '#4B4B4B', // Dark Gray
    '#F6A800', // Gold
    '#00A1E4', // Bright Cyan
    '#B32851', // Strong Pink
]

const lowerCaseHighContrastColors = upperCaseHighContrastColors.map(color => color.toLowerCase())

const highContrastColors = [...upperCaseHighContrastColors, ...lowerCaseHighContrastColors]

const highContrastColorsAndWhite = [...highContrastColors, '#FFFFFF', '#ffffff']

// Lets a shape-bar leave the shape below it visible instead of covering it
const transparent = ['none', 'transparent']

const barShapeColors = [...highContrastColorsAndWhite, ...transparent]

const nyuDataPointValueSchema = z.union([z.number(), z.string()])

const nyuTimeValueSchema = z.union([z.int(), z.string()])

const nyuDataPointSchema = z
    .object({
        time: nyuTimeValueSchema.optional(),
        name: z.string().optional(),
        barChartDataPointGroup: z.string().optional(),
        barChartLabel: z.string().optional(),
    })
    .catchall(nyuDataPointValueSchema)

const nyuDataPointsSchema = z.array(nyuDataPointSchema)

export const nyuDataPointWithParsedTimeSchema = z
    .object({
        time: z.number(),
        year: z.number(),
        tooltipTimeValue: z.string().optional(),
        axisLabelTimeValue: z.string().optional(),
        tooltipTimeLabel: z.string().optional(),
    })
    .catchall(nyuDataPointValueSchema)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const nyuDataPointsWithParsedTimeSchema = z.array(
    nyuDataPointWithParsedTimeSchema
)

const nyuBarShapeTypeSchema = z.enum([
    'circle',
    'square',
    'cross',
    'xcross',
    'triangle',
    'diamond',
])

const nyuLinePropertiesSchema = z.object({
    key: z.string(),
    group: z.string(),
    stack: z.string().optional(),
    label: z.string(),
    color: z.enum(highContrastColors).optional(),
    linetype: z.enum(['solid', 'dotted']).optional(),
    strokewidth: z.int().optional(),
    secondaryAxis: z.boolean().optional(),

    barShapeType: nyuBarShapeTypeSchema.optional(),
    barShapeFill: z.enum(barShapeColors).optional(),
    barShapeStroke: z.enum(barShapeColors).optional(),
    barShapeStrokeWidth: z.int().optional(),
    barShapeScale: z.number().optional(),
    barSize: z.int().optional(),
})

const nyuSeriesPropertiesSchema = z.array(nyuLinePropertiesSchema)

const nyuWrappedStringSchema = z.union([z.string(), z.array(z.string())])
const nyuStringOrEmptyObject = z.union([z.string(), z.object({})])

const nyuAxisDomainItemSchema = z.union([
    z.number(),
    z.enum(['auto', 'dataMin', 'dataMax']),
])

const nyuAxisDomain = z
    .union([
        z.array(nyuAxisDomainItemSchema),
        z.tuple([nyuAxisDomainItemSchema, nyuAxisDomainItemSchema]),
    ])

const nyuReferenceLineSchema = z.object({
    // a number is a fixed position on the value-axis, a string is the id of the
    // series-group whose mean over the displayed data-points is used
    value: z.union([z.number(), z.string()]),
    label: z.string().optional(),
    color: z.enum(highContrastColors).optional(),
    linetype: z.enum(['solid', 'dotted']).optional(),
    strokewidth: z.int().optional(),
    secondaryAxis: z.boolean().optional(),
    hideFromLegend: z.boolean().optional(),
})

const nyuDataPropertiesSchema = z.object({
    format: z.string(),
    timeFormat: z.enum(['y', 'h', 'q', 'm']).optional(),
    axisTitle: z.string(),
    source: nyuWrappedStringSchema,
    axisDomain: nyuAxisDomain.optional(),
    note: nyuStringOrEmptyObject.optional(),
    secondaryAxis: z
        .object({
            format: z.string(),
            axisTitle: z.string(),
            axisDomain: nyuAxisDomain.optional(),
        })
        .optional(),
    hideControls: z
        .array(z.enum(['filter', 'download', 'selector', 'all', 'highContrast']))
        .optional(),
    chartType: z.enum(['line', 'bar']).optional(),
    layout: z.enum(['horizontal', 'vertical']).optional(),
    leftMargin: z.int().optional(),
    defaultStartYear: z.int().optional(),
    defaultEndYear: z.int().optional(),
    referenceLines: z.array(nyuReferenceLineSchema).optional(),
})

const nyuSelectizeProperties = z.strictObject({
    options: z.array(z.string()),
})

export const nyuGraphDataSchema = z.strictObject({
    dataProperties: nyuDataPropertiesSchema,
    seriesProperties: nyuSeriesPropertiesSchema,
    selectizeProperties: nyuSelectizeProperties.optional(),
    data: nyuDataPointsSchema,
})

export const nyuChunkLinkSchema = z.string()

export const nyuGraphDataSchemaOrChunkLinkSchema = z.union([
    nyuGraphDataSchema,
    nyuChunkLinkSchema,
])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const prohibitEmptyObjects = (data: any) => {
    const invalidKeys = Object.entries(data)
        .filter(([, value]) => Object.keys(value!).length === 0)
        .map(([key]) => key)

    if (invalidKeys.length > 0) {
        throw new z.ZodError([
            {
                code: z.ZodIssueCode.custom,
                message:
                    'All leaf nodes must be of type `nyuGraphDataSchema`, maybe there is an empty object ({}) in `dataset`',
                path: invalidKeys,
            },
        ])
    }

    return true
}

const recordUnion = (children: [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]) =>
    z.record(z.string(), z.union(children)).refine(prohibitEmptyObjects)

const nyuDataSetWithChunksSchema = z.union([
    // 0 levels
    nyuGraphDataSchema, // level 0 may not be a chunk link (string) but has to be a `nyuGraphDataSchema`

    // 0 or 1 level
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema, // level 0
        z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema), // level 1
    ]),

    // 0, 1 or 2 levels
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema, // level 0
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema, // level 1
            z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema), // level 2
        ]),
    ]),

    // 0, 1, 2 or 3 levels
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema, // level 0
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema, // level 1
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema, // level 2
                z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema), // level 3
            ]),
        ]),
    ]),

    // 0, 1, 2, 3 or 4 levels (5 total)
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema, // level 0
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema, // level 1        
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema, // level 2
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema, // level 3
                    z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema), // level 4
                ]),
            ]),
        ]),
    ]),

    // 6
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema,
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema,
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema,
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema,
                    recordUnion([
                        nyuGraphDataSchemaOrChunkLinkSchema,
                        z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema),
                    ]),
                ]),
            ]),
        ]),
    ]),

    // 7
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema,
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema,
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema,
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema,
                    recordUnion([
                        nyuGraphDataSchemaOrChunkLinkSchema,
                        recordUnion([
                            nyuGraphDataSchemaOrChunkLinkSchema,
                            z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema),
                        ]),
                    ]),
                ]),
            ]),
        ]),
    ]),

    // 8
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema,
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema,
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema,
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema,
                    recordUnion([
                        nyuGraphDataSchemaOrChunkLinkSchema,
                        recordUnion([
                            nyuGraphDataSchemaOrChunkLinkSchema,
                            recordUnion([
                                nyuGraphDataSchemaOrChunkLinkSchema,
                                z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema),
                            ]),
                        ]),
                    ]),
                ]),
            ]),
        ]),
    ]),

    // 9
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema,
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema,
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema,
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema,
                    recordUnion([
                        nyuGraphDataSchemaOrChunkLinkSchema,
                        recordUnion([
                            nyuGraphDataSchemaOrChunkLinkSchema,
                            recordUnion([
                                nyuGraphDataSchemaOrChunkLinkSchema,
                                recordUnion([
                                    nyuGraphDataSchemaOrChunkLinkSchema,
                                    z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema),
                                ]),
                            ]),
                        ]),
                    ]),
                ]),
            ]),
        ]),
    ]),


    // 10
    recordUnion([
        nyuGraphDataSchemaOrChunkLinkSchema,
        recordUnion([
            nyuGraphDataSchemaOrChunkLinkSchema,
            recordUnion([
                nyuGraphDataSchemaOrChunkLinkSchema,
                recordUnion([
                    nyuGraphDataSchemaOrChunkLinkSchema,
                    recordUnion([
                        nyuGraphDataSchemaOrChunkLinkSchema,
                        recordUnion([
                            nyuGraphDataSchemaOrChunkLinkSchema,
                            recordUnion([
                                nyuGraphDataSchemaOrChunkLinkSchema,
                                recordUnion([
                                    nyuGraphDataSchemaOrChunkLinkSchema,
                                    recordUnion([
                                        nyuGraphDataSchemaOrChunkLinkSchema,
                                        z.record(z.string(), nyuGraphDataSchemaOrChunkLinkSchema),
                                    ]),
                                ]),
                            ]),
                        ]),
                    ]),
                ]),
            ]),
        ]),
    ]),


])

const nyuUiPropertiesSchema = z.strictObject({
    firstSelectorAsTabs: z.boolean().optional(),
    chartHeight: z.strictObject({
        value: z.int(),
        unit: z.enum(['%', 'px']),
    })
        .optional(),
})
    .optional()

export const nyuDataSchema = z.object({
    meta: z.object({
        title: z.string(),
        schema: z.enum(['switch', 'selectize']),
        version: z.string(),
        cite: z.string().optional(),
    }),
    uiProperties: nyuUiPropertiesSchema,
    dataset: nyuDataSetWithChunksSchema,
})

export type NyuDataPoint = z.infer<typeof nyuDataPointSchema>

export type NyuDataPointWithParsedTime = z.infer<
    typeof nyuDataPointWithParsedTimeSchema
>

export type NyuDataPoints = z.infer<typeof nyuDataPointsSchema>

export type NyuDataPointsWithParsedTime = z.infer<
    typeof nyuDataPointsWithParsedTimeSchema
>

export type NyuDataSet = z.infer<typeof nyuDataSetWithChunksSchema>

export type NyuGraphData = z.infer<typeof nyuGraphDataSchema>

export type NyuGraphDataSchemaOrChunkLink = z.infer<
    typeof nyuGraphDataSchemaOrChunkLinkSchema
>

export type NyuChunkLink = z.infer<typeof nyuChunkLinkSchema>

export type NyuData = z.infer<typeof nyuDataSchema>

export type NyuLineProperties = z.infer<typeof nyuLinePropertiesSchema>

export type NyuGroupProperties = {
    id: string
    firstLine: NyuLineProperties
    lines: NyuLineProperties[]
}

export type NyuSeriesProperties = z.infer<typeof nyuSeriesPropertiesSchema>

export type NyuDataProperties = z.infer<typeof nyuDataPropertiesSchema>

export type NyuUiProperties = z.infer<typeof nyuUiPropertiesSchema>

export type NyuTimeValue = z.infer<typeof nyuTimeValueSchema>

export type NyuBarShapeType = z.infer<typeof nyuBarShapeTypeSchema>

export type NyuReferenceLine = z.infer<typeof nyuReferenceLineSchema>
